import React, { useEffect, useState, createElement } from 'react';
import { CheckCircleIcon } from 'lucide-react';
import { getAttributionData } from '../utils/attribution';
// Load gtag script once globally
function loadGtagScript() {
  if (document.getElementById('gtag-script')) return;
  const script = document.createElement('script');
  script.id = 'gtag-script';
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js?id=AW-830901894';
  document.head.appendChild(script);
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.gtag = function () {
    w.dataLayer.push(arguments);
  };
  w.gtag('js', new Date());
  w.gtag('config', 'AW-830901894');
}
const WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/DBw1tkqBTZflFworLNDq/webhook-trigger/99a04f8d-93f8-4b99-b3b2-11f91e4dcd78';
interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ConsultationModal({
  isOpen,
  onClose
}: ConsultationModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [transactionalConsent, setTransactionalConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  // Load gtag on mount
  useEffect(() => {
    loadGtagScript();
  }, []);
  // Fire conversion event when thank you screen shows
  useEffect(() => {
    if (submitted) {
      const w = window as any;
      if (typeof w.gtag === 'function') {
        w.gtag('event', 'conversion', {
          send_to: 'AW-830901894'
        });
      }
    }
  }, [submitted]);
  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSubmitted(false);
      setSubmitting(false);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setTransactionalConsent(false);
      setMarketingConsent(false);
    }
  }, [isOpen]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          phone,
          transactionalConsent,
          marketingConsent,
          source: 'Halo Landing Page',
          ...getAttributionData()
        })
      });
    } catch (err) {
      // Still proceed to thank you / Calendly even if webhook fails
      console.error('Webhook error:', err);
    }
    setSubmitted(true);
    setSubmitting(false);
    window.open('https://calendly.com/be_clinical/30min', '_blank');
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-21">
      <div className="bg-background border border-divider rounded-sm p-8 md:p-10 max-w-md w-full relative max-h-[90vh] overflow-y-auto" data-id="element-22">
        <button onClick={onClose} className="absolute top-4 right-4 text-foreground/40 hover:text-foreground transition-colors text-xl leading-none" aria-label="Close form" data-id="element-23">
          ✕
        </button>

        {submitted ? <div className="flex flex-col items-center text-center py-8" data-id="element-24">
            <CheckCircleIcon size={48} className="text-gold mb-4" data-id="element-25" />
            <h3 className="text-2xl font-serif text-foreground mb-3" data-id="element-26">
              Thank You!
            </h3>
            <p className="text-foreground/60 font-sans text-sm mb-6" data-id="element-27">
              Your consultation request has been received. You'll be redirected
              to schedule your appointment.
            </p>
            <button onClick={onClose} className="px-8 py-3 bg-gold text-white font-sans text-sm uppercase tracking-widest hover:bg-gold/90 transition-colors rounded-md" data-id="element-28">
              Close
            </button>
          </div> : <>
            <h3 className="text-3xl font-serif text-foreground mb-4 text-left" data-id="element-29">
              Book Consultation
            </h3>
            <div className="flex items-start gap-2 mb-1 text-left" data-id="element-30">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-foreground/60 mt-0.5 shrink-0" data-id="element-31">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" data-id="element-32"></path>
                <circle cx="12" cy="10" r="3" data-id="element-33"></circle>
              </svg>
              <div data-id="element-34">
                <p className="text-foreground/70 font-sans text-sm font-medium" data-id="element-35">
                  Be Clinical Med Spa in San Ramon, CA
                </p>
                <p className="text-foreground/50 font-sans text-sm" data-id="element-36">
                  Off of 680 Freeway and Crow Canyon Road
                </p>
              </div>
            </div>
            <div className="border-b border-divider my-6" data-id="element-37"></div>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-id="element-38">
              <div className="grid grid-cols-2 gap-4" data-id="element-39">
                <div data-id="element-40">
                  <label className="block text-foreground/70 font-sans text-xs uppercase tracking-widest mb-2" data-id="element-41">
                    First Name *
                  </label>
                  <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-3 border border-divider bg-background text-foreground font-sans text-sm placeholder:text-foreground/40 focus:outline-none focus:ring-1 focus:ring-gold" required data-id="element-42" />
                </div>
                <div data-id="element-43">
                  <label className="block text-foreground/70 font-sans text-xs uppercase tracking-widest mb-2" data-id="element-44">
                    Last Name *
                  </label>
                  <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-3 border border-divider bg-background text-foreground font-sans text-sm placeholder:text-foreground/40 focus:outline-none focus:ring-1 focus:ring-gold" required data-id="element-45" />
                </div>
              </div>
              <input type="email" placeholder="Email Address" value={email} onChange={e => setEmail(e.target.value)} className="px-4 py-3 border border-divider bg-background text-foreground font-sans text-sm placeholder:text-foreground/40 focus:outline-none focus:ring-1 focus:ring-gold" required data-id="element-46" />
              <input type="tel" placeholder="Phone Number" value={phone} onChange={e => setPhone(e.target.value)} className="px-4 py-3 border border-divider bg-background text-foreground font-sans text-sm placeholder:text-foreground/40 focus:outline-none focus:ring-1 focus:ring-gold" data-id="element-47" />

              {/* Consent Checkboxes */}
              <label className="flex items-start gap-3 text-left cursor-pointer" data-id="element-48">
                <input type="checkbox" required checked={transactionalConsent} onChange={e => setTransactionalConsent(e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-gold border-divider" data-id="element-49" />
                <span className="text-foreground/60 font-sans text-xs leading-relaxed" data-id="element-50">
                  By checking this box, I consent to receive non-marketing text
                  messages from Be Clinical about appointment confirmations,
                  service updates, and scheduling. Message frequency varies.
                  Message &amp; data rates may apply. Reply STOP to unsubscribe
                  at any time.
                </span>
              </label>

              <label className="flex items-start gap-3 text-left cursor-pointer" data-id="element-51">
                <input type="checkbox" checked={marketingConsent} onChange={e => setMarketingConsent(e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-gold border-divider" data-id="element-52" />
                <span className="text-foreground/60 font-sans text-xs leading-relaxed" data-id="element-53">
                  By checking this box, I consent to receive marketing text
                  messages from Be Clinical including special offers, discounts,
                  and promotional updates at the phone number provided.
                  Frequency may vary. Message &amp; data rates may apply. Reply
                  STOP to unsubscribe at any time.
                </span>
              </label>

              <button type="submit" disabled={submitting} className="mt-2 px-6 py-3 bg-gold text-white font-sans text-sm uppercase tracking-widest hover:bg-gold/90 transition-colors rounded-md disabled:opacity-50" data-id="element-54">
                {submitting ? 'Submitting...' : 'Continue to Scheduling'}
              </button>
            </form>
          </>}
      </div>
    </div>;
}